import { BookOpen, CheckCheck, Inbox, Quote } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { request } from "../api";
import { ErrorCard, Loading } from "../components/AsyncState";
import type { Annotation, Book } from "../types";
import { errorMessage, formatDate } from "../utils";

const typeLabel: Record<string, string> = {
  unknown_word: "生词",
  difficult_usage: "难点用法",
  difficult_sentence: "难句",
  valuable_expression: "表达",
  note: "笔记"
};

export function InboxPage() {
  const [items, setItems] = useState<Array<Annotation & { book?: Book }> | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const books = await request<Book[]>("/api/books");
      const groups = await Promise.all(
        books.map(async (book) =>
          (await request<Annotation[]>(`/api/reading/books/${book.id}/annotations`)).map(
            (annotation) => ({ ...annotation, book })
          )
        )
      );
      setItems(groups.flat().sort((a, b) => b.updated_at.localeCompare(a.updated_at)));
      setError("");
    } catch (reason) {
      setError(errorMessage(reason));
    }
  }, []);
  useEffect(() => void load(), [load]);

  if (error) return <ErrorCard message={error} retry={() => void load()} />;
  if (!items) return <Loading label="正在汇总阅读标记" />;

  return (
    <div className="stack">
      <header className="page-heading">
        <div><span className="eyebrow"><Inbox size={14} /> Après la lecture</span><h1>阅读后再集中处理</h1><p>这里承接查词、理解和整理，不让它们打断第一遍阅读。</p></div>
      </header>
      {items.length ? (
        <div className="inbox-list">
          {items.map((item) => (
            <article key={item.id}>
              <div className={`annotation-icon annotation-icon--${item.annotation_type}`}>
                {item.annotation_type === "valuable_expression" ? <Quote size={18} /> : <BookOpen size={18} />}
              </div>
              <div className="inbox-list__copy">
                <div><span className="status-pill">{typeLabel[item.annotation_type]}</span><small>{item.book?.title} · {formatDate(item.created_at)}</small></div>
                <h3>{item.selected_text}</h3>
                {(item.prefix || item.suffix) && <p>…{item.prefix}<mark>{item.selected_text}</mark>{item.suffix}…</p>}
                {item.note && <p className="annotation-note">{item.note}</p>}
              </div>
              <Link className="secondary-button" to={`/read/${item.book_id}`}>回到原文</Link>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-state"><CheckCheck size={35} /><h3>待整理箱是空的</h3><p>阅读时选中文本并快速标记，内容会出现在这里。</p></div>
      )}
    </div>
  );
}
